import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { PhoneCall, Phone, Clock, AlertTriangle, PhoneMissed, CheckCircle2, Search } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardBody } from '@/components/ui/Card';
import { StatCard } from '@/components/ui/StatCard';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { useSipPhoneContext } from '@/contexts/SipPhoneContext';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

type Status = 'pending' | 'reached' | 'no_answer' | 'unreachable' | 'not_needed';
type Outcome = 'reached' | 'no_answer' | 'invalid_number' | 'declined';
type Tab = 'due' | 'upcoming' | 'completed' | 'all';

const STATUS_TONE: Record<Status, 'primary' | 'success' | 'warning' | 'error' | 'neutral'> = {
  pending: 'primary',
  reached: 'success',
  no_answer: 'warning',
  unreachable: 'error',
  not_needed: 'neutral',
};
const STATUS_LABEL: Record<Status, string> = {
  pending: 'Pending',
  reached: 'Reached',
  no_answer: 'No Answer',
  unreachable: 'Unreachable',
  not_needed: 'Not Needed',
};
const OUTCOME_OPTIONS: { value: Outcome; label: string }[] = [
  { value: 'reached', label: 'Reached — spoke with customer' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'invalid_number', label: 'Invalid Number' },
  { value: 'declined', label: 'Declined to talk' },
];

interface FollowupRow {
  id: string;
  order_id: string;
  customer_phone: string;
  customer_name: string | null;
  status: Status;
  next_attempt_at: string;
  attempt_count: number;
  feedback: string | null;
  created_at: string;
  core_orders: {
    order_no: string;
    placed_at: string;
    core_order_items: { qty: number; core_products: { name_en: string | null } | null }[];
  } | null;
}

interface AttemptRow {
  id: string;
  outcome: Outcome;
  note: string | null;
  called_at: string;
}

function productSummary(row: FollowupRow): string {
  const items = row.core_orders?.core_order_items ?? [];
  if (items.length === 0) return '—';
  return items.map((i) => `${i.core_products?.name_en ?? 'Product'} ×${i.qty}`).join(', ');
}

// One shared queue: every delivered order (except pharmacy_b2b resellers,
// excluded at the DB trigger level — see core_customer_followups migration)
// lands here automatically. Staff work the "Due" tab top to bottom, log
// each call's outcome, and a "No Answer" quietly re-queues itself a few
// days out instead of being lost.
export default function AdminFollowup() {
  const { push } = useToast();
  const phone = useSipPhoneContext();

  const callNumber = (number: string) => {
    if (phone.registrationStatus !== 'registered') {
      push('error', phone.hasCredentials ? 'Phone is not connected yet — check the status in Communications.' : 'No SIP extension set up for you yet — ask an admin to set one up in ERP.');
      return;
    }
    phone.call(number);
  };
  const [rows, setRows] = useState<FollowupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('due');
  const [search, setSearch] = useState('');

  const [detail, setDetail] = useState<FollowupRow | null>(null);
  const [attempts, setAttempts] = useState<AttemptRow[]>([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>('reached');
  const [note, setNote] = useState('');
  const [feedback, setFeedback] = useState('');
  const [nextFollowupDays, setNextFollowupDays] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    supabase
      .from('core_customer_followups')
      .select('id, order_id, customer_phone, customer_name, status, next_attempt_at, attempt_count, feedback, created_at, core_orders(order_no, placed_at, core_order_items(qty, core_products(name_en)))')
      .order('next_attempt_at', { ascending: true })
      .limit(2000)
      .then(({ data, error }) => {
        if (error) push('error', error.message);
        setRows((data as unknown as FollowupRow[]) ?? []);
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const now = Date.now();
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const endOfToday = new Date(startOfToday);
  endOfToday.setDate(endOfToday.getDate() + 1);
  const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);

  const isOpenStatus = (s: Status) => s === 'pending' || s === 'no_answer';

  const dueToday = rows.filter((r) => isOpenStatus(r.status) && new Date(r.next_attempt_at) < endOfToday);
  const overdue = rows.filter((r) => isOpenStatus(r.status) && new Date(r.next_attempt_at) < startOfToday);
  const noAnswer = rows.filter((r) => r.status === 'no_answer');
  const completedThisWeek = rows.filter((r) => r.status === 'reached' && new Date(r.created_at) >= sevenDaysAgo);

  const filtered = useMemo(() => {
    let base = rows;
    if (tab === 'due') base = rows.filter((r) => isOpenStatus(r.status) && new Date(r.next_attempt_at) < endOfToday);
    else if (tab === 'upcoming') base = rows.filter((r) => isOpenStatus(r.status) && new Date(r.next_attempt_at) >= endOfToday);
    else if (tab === 'completed') base = rows.filter((r) => r.status === 'reached' || r.status === 'not_needed' || r.status === 'unreachable');
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) => (r.customer_name ?? '').toLowerCase().includes(q) || r.customer_phone.includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, tab, search, endOfToday.getTime()]);

  const openDetail = (row: FollowupRow) => {
    setDetail(row);
    setOutcome('reached');
    setNote('');
    setFeedback(row.feedback ?? '');
    setNextFollowupDays('');
    setAttemptsLoading(true);
    supabase
      .from('core_followup_attempts')
      .select('id, outcome, note, called_at')
      .eq('followup_id', row.id)
      .order('called_at', { ascending: false })
      .then(({ data }) => {
        setAttempts((data as AttemptRow[]) ?? []);
        setAttemptsLoading(false);
      });
  };

  const saveAttempt = async (e: FormEvent) => {
    e.preventDefault();
    if (!detail) return;
    const days = Number(nextFollowupDays);
    if (!Number.isFinite(days) || days <= 0) {
      push('error', 'Enter how many days until the next followup');
      return;
    }
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error: attemptErr } = await supabase.from('core_followup_attempts').insert({
        followup_id: detail.id,
        outcome,
        note: note || null,
        called_by: user?.id ?? null,
      });
      if (attemptErr) throw attemptErr;

      // Whatever the outcome, every logged call schedules the next one —
      // status reopens to 'pending' so the row comes back into the queue
      // on the date staff chose. "Mark as not needed" (separate action)
      // is the only way to close a followup out for good.
      const patch: Record<string, unknown> = {
        status: 'pending' as Status,
        attempt_count: detail.attempt_count + 1,
        next_attempt_at: new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString(),
      };
      if (outcome === 'reached') {
        patch.feedback = feedback || null;
      }
      const { error: updateErr } = await supabase.from('core_customer_followups').update(patch).eq('id', detail.id);
      if (updateErr) throw updateErr;

      push('success', `Logged — next followup in ${days} day(s)`);
      setDetail(null);
      load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const markNotNeeded = async (row: FollowupRow) => {
    const { error } = await supabase.from('core_customer_followups').update({ status: 'not_needed' }).eq('id', row.id);
    if (error) { push('error', error.message); return; }
    push('success', 'Marked as not needed');
    load();
  };

  const TABS: { key: Tab; label: string }[] = [
    { key: 'due', label: `Due (${dueToday.length})` },
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'completed', label: 'Completed' },
    { key: 'all', label: 'All' },
  ];

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[18px] font-bold text-ink">Customer Followup</h1>
            <p className="text-[12px] text-ink-faint mt-0.5">Call customers after delivery, log feedback, retry no-answers</p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Due Today" value={dueToday.length} icon={<Clock className="w-4 h-4" />} accent="primary" />
          <StatCard label="Overdue" value={overdue.length} icon={<AlertTriangle className="w-4 h-4" />} accent="error" />
          <StatCard label="No Answer (retry)" value={noAnswer.length} icon={<PhoneMissed className="w-4 h-4" />} accent="warning" />
          <StatCard label="Completed This Week" value={completedThisWeek.length} icon={<CheckCircle2 className="w-4 h-4" />} accent="success" />
        </div>

        <Card>
          <CardBody className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="inline-flex items-center gap-1 bg-surface-muted rounded-xl p-1">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`px-3.5 py-1.5 rounded-lg text-[12.5px] font-semibold cursor-pointer transition-colors ${
                      tab === t.key ? 'bg-white text-primary-800 shadow-sm' : 'text-ink-faint hover:text-ink'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-disabled" />
                <Input placeholder="Search name or phone" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" />
              </div>
            </div>

            {loading ? (
              <Spinner />
            ) : filtered.length === 0 ? (
              <EmptyState title="Nothing here" hint="No followups match this tab." />
            ) : (
              <div className="space-y-2">
                {filtered.map((r) => (
                  <div
                    key={r.id}
                    onClick={() => openDetail(r)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-border-subtle hover:bg-surface-muted cursor-pointer transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold text-ink truncate">{r.customer_name || '(no name)'}</p>
                      <p className="text-[11.5px] text-ink-faint truncate">{productSummary(r)} · {r.core_orders?.order_no}</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        callNumber(r.customer_phone);
                      }}
                      title="Call now"
                      className="flex items-center gap-1.5 text-[12.5px] font-semibold text-primary-800 hover:underline flex-shrink-0 cursor-pointer"
                    >
                      <Phone className="w-3.5 h-3.5" /> {r.customer_phone}
                    </button>
                    <span className="text-[11px] text-ink-faint flex-shrink-0 hidden sm:inline">
                      {r.attempt_count > 0 ? `${r.attempt_count} attempt(s)` : 'No attempts yet'}
                    </span>
                    <span className="text-[11px] text-ink-faint flex-shrink-0 hidden md:inline w-24">
                      Due {new Date(r.next_attempt_at).toLocaleDateString()}
                    </span>
                    <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <Modal
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail?.customer_name || 'Customer Followup'}
        subtitle={detail?.customer_phone}
        icon={<PhoneCall className="w-4 h-4" />}
        busy={saving}
        maxWidth="max-w-xl"
        footer={
          <>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setDetail(null)} disabled={saving}>Close</Button>
            <Button type="submit" form="followup-attempt-form" className="flex-1" loading={saving}>Log Call</Button>
          </>
        }
      >
        {detail && (
          <div className="space-y-4">
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() => callNumber(detail.customer_phone)}
            >
              <Phone className="w-3.5 h-3.5" /> Call {detail.customer_phone}
            </Button>

            <div className="text-[12.5px] text-ink-muted space-y-1">
              <p><span className="text-ink-faint">Order:</span> {detail.core_orders?.order_no}</p>
              <p><span className="text-ink-faint">Product(s):</span> {productSummary(detail)}</p>
              <p><span className="text-ink-faint">Current status:</span> <Badge tone={STATUS_TONE[detail.status]}>{STATUS_LABEL[detail.status]}</Badge></p>
            </div>

            {detail.status !== 'not_needed' && (
              <button
                type="button"
                onClick={() => markNotNeeded(detail)}
                className="text-[11.5px] text-ink-faint hover:text-error underline cursor-pointer"
              >
                Mark as not needed (skip this one)
              </button>
            )}

            <div>
              <p className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-ink-faint mb-2">Call History</p>
              {attemptsLoading ? (
                <Spinner />
              ) : attempts.length === 0 ? (
                <p className="text-[12px] text-ink-faint">No calls logged yet.</p>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {attempts.map((a) => (
                    <div key={a.id} className="text-[12px] text-ink-muted flex items-start gap-2">
                      <Badge tone={a.outcome === 'reached' ? 'success' : 'warning'}>{a.outcome.replace('_', ' ')}</Badge>
                      <span className="flex-1">{a.note}</span>
                      <span className="text-ink-faint flex-shrink-0">{new Date(a.called_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <form id="followup-attempt-form" onSubmit={saveAttempt} className="space-y-3.5 border-t border-border-subtle pt-4">
              <Field label="Call Outcome">
                <Select value={outcome} onChange={(e) => setOutcome(e.target.value as Outcome)}>
                  {OUTCOME_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Note (optional)">
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything worth remembering about this call" />
              </Field>
              <Field label="Next followup in (days) — required">
                <Input
                  type="number"
                  min={1}
                  required
                  value={nextFollowupDays}
                  onChange={(e) => setNextFollowupDays(e.target.value)}
                  placeholder="e.g. 7"
                />
              </Field>
              {outcome === 'reached' && (
                <Field label="Product Feedback">
                  <Textarea rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="How is the product working for them?" />
                </Field>
              )}
            </form>
          </div>
        )}
      </Modal>
    </AppShell>
  );
}
